import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

const { TextArea } = Input;
const { Option } = Select;


const prefixSelector = (
    <Form.Item name="prefix" noStyle  >
        <Select
            style={{ width: 100, }}
            dropdownStyle={{ borderRadius: 10 }}
        >
            <Option value="91">+91</Option>
            <Option value="977">+977</Option>
            <Option value="94">+94</Option>
            <Option value="7">+7</Option>
        </Select>
    </Form.Item>
);

interface ProductModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        product_id?: number;
        product_name?: string;
        product_code?: string;       // added
        product_price?: number;      // added
    } | null;
    isEditMode?: boolean;
}

const ProductModal: React.FC<ProductModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // Handle form submit
    const handleAddProduct = async (values: any) => {
        try {
            const dataVal = {
                product_name: values.product_name,
                product_code: values.product_code,
                product_price: parseFloat(values.product_price),  // convert to number
                ...(initialValues?.product_id && { product_id: initialValues.product_id }),
            };
            const url = isEditMode ? 'edit-product' : 'add-product';
            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });

            onCreate(data);
            showSuccess(isEditMode ? 'Product updated successfully!' : 'Product created successfully!');
            // form.resetFields();

            if (isModalClose) {
                onCancel();
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} product`);
        }
    };


    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                product_name: initialValues.product_name,
                product_code: initialValues.product_code,
                product_price: initialValues.product_price?.toString(), // convert number to string for Input
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);




    return (
        <Modal
            title={isEditMode ? 'Edit Product' : 'Add New Product'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddProduct}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="product_name"
                            className="px-8"
                            label={
                                <span>
                                    Product Name&nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>(Max 255 characters)</span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter the Product Name' },
                                { max: 255, message: 'Maximum 255 characters allowed' },
                            ]}
                        >
                            <Input showCount maxLength={255} placeholder="e.g., Product" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="product_code"
                            label="Product Code"
                            rules={[
                                { required: true, message: 'Please enter the Product Code' },
                                { max: 50, message: 'Maximum 50 characters allowed' },
                            ]}
                        >
                            <Input maxLength={50} placeholder="e.g., PROD1234" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="product_price"
                            label="Product Price"
                            rules={[
                                { required: true, message: 'Please enter the Product Price' },
                                {
                                    pattern: /^\d+(\.\d{1,2})?$/,
                                    message: 'Enter a valid price (up to 2 decimals)',
                                },
                            ]}
                        >
                            <Input placeholder="e.g., 199.99" />
                        </Form.Item>
                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Product
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ProductModal;
